import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import {
  IPurviewCVCCustomersResponse,
  IPurviewCVCCustomerNotesResponse,
  IPurviewCVCAIChatRequest,
  IPurviewCVCAIChatResponse
} from "../components/PurviewCVC/PurviewCVC.interfaces";

/**
 * API slice for Purview CVC (Customer Value Conversation) functionality
 * Provides endpoint to fetch Purview CVC customer data from Kusto
 *
 * Caching Strategy:
 * - keepUnusedDataFor: 300 seconds (5 minutes) - data stays cached even when component unmounts
 * - refetchOnMountOrArgChange: false - prevents refetch on every tab switch
 * - refetchOnFocus: false - prevents refetch when window regains focus
 */
export const purviewCVCAPI = createApi({
  reducerPath: "purviewCVC",
  tagTypes: ["PurviewCVCCustomers", "PurviewCVCCustomerNotes"],
  baseQuery: baseQueryWithReauth,
  // Global cache settings - keep data for 5 minutes to avoid repeated API calls on tab switches
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    /**
     * Fetches all Purview CVC customers from CRM_DimCustomerProduct and CRM_DimCustomerProductNotes
     * Returns customers list and Kusto query link for exploring data
     */
    getPurviewCVCCustomers: builder.query<IPurviewCVCCustomersResponse, void>({
      query: () => ({
        url: "purview-cvc/customers",
        method: "GET"
      }),
      providesTags: ["PurviewCVCCustomers"],
      keepUnusedDataFor: 300
    }),

    /**
     * Fetches all notes for a Purview CVC customer product engagement
     * Returns notes list ordered by most recent modification date
     */
    getPurviewCVCCustomerNotes: builder.query<IPurviewCVCCustomerNotesResponse, { customerProductId: string }>({
      query: ({ customerProductId }) => ({
        url: `purview-cvc/customer/${customerProductId}/notes`,
        method: "GET"
      }),
      providesTags: (_result, _error, { customerProductId }) => [
        { type: "PurviewCVCCustomerNotes", id: customerProductId }
      ],
      keepUnusedDataFor: 60 // Cache notes for 1 minute
    }),

    /**
     * Fetches all notes for multiple Purview CVC customer product engagements in a single batch
     * Used for export functionality to avoid N+1 API calls
     */
    getPurviewCVCCustomerNotesBatch: builder.mutation<IPurviewCVCCustomerNotesResponse, { customerProductIds: string[] }>({
      query: ({ customerProductIds }) => ({
        url: "purview-cvc/customers/notes/batch",
        method: "POST",
        body: { customerProductIds }
      })
    }),

    /**
     * AI Assistant Chat - Ask questions about Purview CVC customer data
     * Sends the user's question along with summarized data context to OpenAI
     * Returns AI-generated response based on the data
     */
    askPurviewCVCAIAssistant: builder.mutation<IPurviewCVCAIChatResponse, IPurviewCVCAIChatRequest>({
      query: (request) => ({
        url: "purview-cvc/ai-assistant/chat",
        method: "POST",
        body: request
      })
    })
  })
});

export const {
  useGetPurviewCVCCustomersQuery,
  useLazyGetPurviewCVCCustomerNotesQuery,
  useGetPurviewCVCCustomerNotesBatchMutation,
  useAskPurviewCVCAIAssistantMutation
} = purviewCVCAPI;
